import React from "react";
import {
  Settings,
  LogOut,
  ChevronDown,
  User,
  Bell,
  GraduationCap,
  Menu,
  X,
  Building2,
  Car,
} from "lucide-react";
import { Link, useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { userService } from "../../services/userService";
import { getInstitutionInitials } from "../../utils/institutionBranding";

const AdminLayout = ({
  children,
  title = "Admin Dashboard",
  onOpenFormative,
  onOpenService,
}) => {
  const { user, signOut } = useAuth();
  const { institutionSettings: institution, theme } = useTheme();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);
  const [activeNavMenu, setActiveNavMenu] = React.useState(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [userProfile, setUserProfile] = React.useState(null);
  const [showEditProfile, setShowEditProfile] = React.useState(false);
  const institutionInitials = getInstitutionInitials(institution);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeNavMenu && !event.target.closest(".nav-dropdown-container")) {
        setActiveNavMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeNavMenu]);

  React.useEffect(() => {
    const fetchProfile = async () => {
      if (user?.id) {
        const profile = await userService.getCurrentUserProfile(user.id);
        setUserProfile(profile);
      }
    };
    fetchProfile();
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  const openService = (path) => {
    setActiveNavMenu(null);
    setMobileOpen(false);
    if (onOpenService) {
      const [, query = ""] = String(path || "").split("?");
      const workspace = new URLSearchParams(query).get("workspace");
      if (workspace) {
        onOpenService(workspace);
        return;
      }
    }
    navigate(path);
  };

  const [isSaving, setIsSaving] = React.useState(false);
  const [formData, setFormData] = React.useState({ full_name: "" });

  React.useEffect(() => {
    if (userProfile) {
      setFormData({ full_name: userProfile.full_name || "" });
    }
  }, [userProfile]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const { success, error } = await userService.updateUserProfile(user.id, {
        full_name: formData.full_name,
      });

      if (success) {
        // Update local state to reflect changes immediately
        setUserProfile((prev) => ({ ...prev, full_name: formData.full_name }));
        setShowEditProfile(false);
        // Optional: Show success message
      } else {
        console.error("Failed to update profile:", error);
        alert("Failed to save changes. Please try again.");
      }
    } catch (err) {
      console.error("Unexpected error saving profile:", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header
        className="sticky top-0 z-[100] bg-primary text-primary-foreground shadow-lg"
        style={{
          backgroundColor:
            theme?.primary || institution?.theme_primary || undefined,
        }}
      >
        <div className="mx-auto flex min-h-16 max-w-[1600px] items-center gap-5 px-4 lg:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-5">
            <Link
              to="/"
              aria-label={`${institutionInitials} portal home`}
              className="flex shrink-0 items-center gap-3 transition-opacity hover:opacity-80"
            >
              {institution?.logo_url ? (
                <img
                  src={institution.logo_url}
                  alt="Institution logo"
                  className="h-10 w-10 rounded-md bg-white object-contain p-1"
                />
              ) : (
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary-foreground/20">
                  <GraduationCap className="h-6 w-6" />
                </span>
              )}
              <span className="text-sm font-bold tracking-[0.18em]">
                {institutionInitials}
              </span>
            </Link>

            <nav className="hidden min-w-0 flex-1 items-center justify-center gap-3 lg:flex 2xl:gap-7">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:text-primary-foreground ${isActive ? "text-primary-foreground" : "text-primary-foreground/80"}`
                }
              >
                Dashboard
              </NavLink>

              {/* Academic Management Dropdown */}
              <div
                className="relative nav-dropdown-container"
                onMouseEnter={() => setActiveNavMenu("academic")}
                onMouseLeave={() => setActiveNavMenu(null)}
              >
                <button
                  onClick={() =>
                    setActiveNavMenu(
                      activeNavMenu === "academic" ? null : "academic",
                    )
                  }
                  aria-expanded={activeNavMenu === "academic"}
                  aria-haspopup="menu"
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary-foreground ${activeNavMenu === "academic" ? "text-primary-foreground" : "text-primary-foreground/80"}`}
                >
                  Academic Management
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${activeNavMenu === "academic" ? "rotate-180" : ""}`}
                  />
                </button>

                {activeNavMenu === "academic" && (
                  <div className="absolute left-0 top-full pt-2 w-64 z-[110]">
                    <div className="rounded-xl border bg-card text-card-foreground shadow-lg animate-in fade-in zoom-in-95 duration-150">
                      <div className="p-2 space-y-1" role="menu">
                        <NavLink
                          to="/admissions"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Admissions
                        </NavLink>
                        <NavLink
                          to="/academic-management"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Programs & Units
                        </NavLink>
                        <NavLink
                          to="/finance"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Finance
                        </NavLink>
                        <NavLink
                          to="/student-requests"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Student Requests
                        </NavLink>
                        <div className="h-px bg-border my-1" />
                        <NavLink
                          to="/timetable-management"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Timetable Management
                        </NavLink>
                        <NavLink
                          to="/poe-management-hub"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          POE Management Hub
                        </NavLink>
                        <div className="px-3 py-1 text-xs font-semibold text-muted-foreground mt-2">
                          Assessments
                        </div>
                        <NavLink
                          to="/formative-assessment-management"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={(event) => {
                            if (onOpenFormative) {
                              event.preventDefault();
                              onOpenFormative();
                            }
                            setActiveNavMenu(null);
                          }}
                        >
                          Formative Assessment
                        </NavLink>
                        <NavLink
                          to="/summative-assessment-portal"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Summative Assessment
                        </NavLink>
                        <NavLink
                          to="/summative-assessment-schedule-management"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Summative Timetable
                        </NavLink>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Hierarchy Settings Dropdown */}
              <div
                className="relative nav-dropdown-container"
                onMouseEnter={() => setActiveNavMenu("hierarchy")}
                onMouseLeave={() => setActiveNavMenu(null)}
              >
                <button
                  onClick={() =>
                    setActiveNavMenu(
                      activeNavMenu === "hierarchy" ? null : "hierarchy",
                    )
                  }
                  aria-expanded={activeNavMenu === "hierarchy"}
                  aria-haspopup="menu"
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary-foreground ${activeNavMenu === "hierarchy" ? "text-primary-foreground" : "text-primary-foreground/80"}`}
                >
                  Hierarchy Settings
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${activeNavMenu === "hierarchy" ? "rotate-180" : ""}`}
                  />
                </button>

                {activeNavMenu === "hierarchy" && (
                  <div className="absolute left-0 top-full pt-2 w-48 z-[110]">
                    <div className="rounded-xl border bg-card text-card-foreground shadow-lg animate-in fade-in zoom-in-95 duration-150">
                      <div className="p-2 space-y-1" role="menu">
                        <NavLink
                          to="/hierarchy/faculty"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Faculty
                        </NavLink>
                        <NavLink
                          to="/hierarchy/department"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Department
                        </NavLink>
                        <NavLink
                          to="/hierarchy/section"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Section
                        </NavLink>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* User Management Dropdown */}
              <div
                className="relative nav-dropdown-container"
                onMouseEnter={() => setActiveNavMenu("users")}
                onMouseLeave={() => setActiveNavMenu(null)}
              >
                <button
                  onClick={() =>
                    setActiveNavMenu(activeNavMenu === "users" ? null : "users")
                  }
                  aria-expanded={activeNavMenu === "users"}
                  aria-haspopup="menu"
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary-foreground ${activeNavMenu === "users" ? "text-primary-foreground" : "text-primary-foreground/80"}`}
                >
                  User Management
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${activeNavMenu === "users" ? "rotate-180" : ""}`}
                  />
                </button>

                {activeNavMenu === "users" && (
                  <div className="absolute left-0 top-full pt-2 w-48 z-[110]">
                    <div className="rounded-xl border bg-card text-card-foreground shadow-lg animate-in fade-in zoom-in-95 duration-150">
                      <div className="p-2 space-y-1" role="menu">
                        <NavLink
                          to="/students"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Student Management
                        </NavLink>
                        <NavLink
                          to="/enhanced-user-management-system"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          User Management System
                        </NavLink>
                        <NavLink
                          to="/staff-management"
                          className="block px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                          onClick={() => setActiveNavMenu(null)}
                        >
                          Staff Management
                        </NavLink>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Services Dropdown */}
              <div
                className="relative nav-dropdown-container"
                onMouseEnter={() => setActiveNavMenu("services")}
                onMouseLeave={() => setActiveNavMenu(null)}
              >
                <button
                  onClick={() =>
                    setActiveNavMenu(
                      activeNavMenu === "services" ? null : "services",
                    )
                  }
                  aria-expanded={activeNavMenu === "services"}
                  aria-haspopup="menu"
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary-foreground ${activeNavMenu === "services" ? "text-primary-foreground" : "text-primary-foreground/80"}`}
                >
                  Services
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${activeNavMenu === "services" ? "rotate-180" : ""}`}
                  />
                </button>

                {activeNavMenu === "services" && (
                  <div className="absolute left-0 top-full z-[110] w-60 pt-2">
                    <div className="rounded-xl border bg-card text-card-foreground shadow-lg animate-in fade-in zoom-in-95 duration-150">
                      <div className="space-y-1 p-2" role="menu">
                        <button
                          type="button"
                          onClick={() =>
                            openService(
                              "/non-teaching-dashboard?workspace=hostel&view=overview&viewAs=hostel_officer",
                            )
                          }
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                        >
                          <Building2 className="h-4 w-4 text-indigo-600" />
                          Hostel ORRA
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            openService(
                              "/non-teaching-dashboard?workspace=transport&view=overview&viewAs=transport_officer",
                            )
                          }
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                        >
                          <Car className="h-4 w-4 text-indigo-600" />
                          Transport
                        </button>
                        <div className="my-1 h-px bg-border" />
                        <button
                          type="button"
                          onClick={() =>
                            openService(
                              "/non-teaching-dashboard?workspace=estates&view=overview&viewAs=estates_officer",
                            )
                          }
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                          >
                          <Building2 className="h-4 w-4 text-indigo-600" />
                          Estates
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* System Settings Dropdown */}
              <div
                className="relative nav-dropdown-container"
                onMouseEnter={() => setActiveNavMenu("system-settings")}
                onMouseLeave={() => setActiveNavMenu(null)}
              >
                <button
                  type="button"
                  onClick={() =>
                    setActiveNavMenu(
                      activeNavMenu === "system-settings"
                        ? null
                        : "system-settings",
                    )
                  }
                  aria-expanded={activeNavMenu === "system-settings"}
                  aria-haspopup="menu"
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary-foreground ${activeNavMenu === "system-settings" ? "text-primary-foreground" : "text-primary-foreground/80"}`}
                >
                  System Settings
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${activeNavMenu === "system-settings" ? "rotate-180" : ""}`}
                  />
                </button>

                {activeNavMenu === "system-settings" && (
                  <div className="absolute right-0 top-full z-[110] w-56 pt-2">
                    <div className="rounded-xl border bg-card text-card-foreground shadow-lg animate-in fade-in zoom-in-95 duration-150">
                      <div className="space-y-1 p-2" role="menu">
                        {[
                          ["profile", "Institution"],
                          ["enrolment", "Enrolment"],
                          ["calendar", "Calendar"],
                          ["timetable", "Timetable"],
                          ["finances", "Billing"],
                          ["theme", "Appearance"],
                        ].map(([section, label]) => (
                          <NavLink
                            key={section}
                            to="/system-settings"
                            state={{ section }}
                            className="block rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent"
                            onClick={() => setActiveNavMenu(null)}
                          >
                            {label}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </nav>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              className="hidden rounded-full p-2 text-primary-foreground/75 transition-colors hover:bg-white/10 hover:text-primary-foreground lg:block"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" />
            </button>

            <button
              className="rounded-lg p-2 hover:bg-white/10 lg:hidden"
              onClick={() => setMobileOpen((value) => !value)}
              aria-label="Toggle navigation"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>

            <div className="relative hidden lg:block">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex max-w-64 items-center gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-white/10"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15 text-primary-foreground">
                  <User className="h-4 w-4" />
                </div>
                <div className="min-w-0 text-left">
                  <p className="max-w-44 truncate text-sm font-semibold leading-none">
                    {userProfile?.full_name ||
                      user?.user_metadata?.full_name ||
                      "Administrator"}
                  </p>
                  <p className="mt-1 text-xs capitalize text-primary-foreground/65">
                    {String(
                      userProfile?.primary_role || "System Admin",
                    ).replaceAll("_", " ")}
                  </p>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-primary-foreground/65 transition-transform ${showProfileMenu ? "rotate-180" : ""}`}
                />
              </button>

              {/* Dropdown Menu */}
              {showProfileMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border bg-card text-card-foreground shadow-lg z-[110] animate-in fade-in zoom-in-95 duration-200">
                  <div className="p-2 space-y-1">
                    <div className="px-2 py-1.5 text-sm font-semibold border-b mb-1">
                      My Account
                    </div>
                    <button
                      onClick={() => {
                        setShowEditProfile(true);
                        setShowProfileMenu(false);
                      }}
                      className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                    >
                      <Settings className="h-4 w-4" />
                      <span>Edit Profile</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Log Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {mobileOpen && (
          <nav className="max-h-[75vh] overflow-y-auto border-t border-white/10 bg-white px-5 py-3 text-slate-800 lg:hidden">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="block rounded-md px-3 py-2 text-sm font-semibold hover:bg-slate-100"
            >
              Dashboard
            </Link>
            <p className="mt-2 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Academic management
            </p>
            {[
              ["Admissions", "/admissions"],
              ["Programs & Units", "/academic-management"],
              ["Finance", "/finance"],
              ["Student Requests", "/student-requests"],
              ["Timetable Management", "/timetable-management"],
              ["POE Management Hub", "/poe-management-hub"],
              ["Formative Assessment", "/formative-assessment-management"],
              ["Summative Assessment", "/summative-assessment-portal"],
              [
                "Summative Timetable",
                "/summative-assessment-schedule-management",
              ],
            ].map(([label, href]) => (
              <Link
                key={href}
                to={href}
                onClick={() => setMobileOpen(false)}
                className="block rounded-md px-3 py-2 text-sm hover:bg-slate-100"
              >
                {label}
              </Link>
            ))}
            <p className="mt-2 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Services
            </p>
            {[
              [
                "Hostel ORRA",
                "/non-teaching-dashboard?workspace=hostel&view=overview&viewAs=hostel_officer",
              ],
              [
                "Transport",
                "/non-teaching-dashboard?workspace=transport&view=overview&viewAs=transport_officer",
              ],
              [
                "Estates",
                "/non-teaching-dashboard?workspace=estates&view=overview&viewAs=estates_officer",
              ],
            ].map(([label, href]) => (
              <button
                key={href}
                type="button"
                onClick={() => openService(href)}
                className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-slate-100"
              >
                {label}
              </button>
            ))}
            <p className="mt-2 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Administration
            </p>
            {[
              ["Faculty", "/hierarchy/faculty"],
              ["Departments", "/hierarchy/department"],
              ["Sections", "/hierarchy/section"],
              ["Student Management", "/students"],
              ["User Management", "/enhanced-user-management-system"],
              ["Staff Management", "/staff-management"],
              ["System Settings", "/system-settings"],
            ].map(([label, href]) => (
              <Link
                key={href}
                to={href}
                onClick={() => setMobileOpen(false)}
                className="block rounded-md px-3 py-2 text-sm hover:bg-slate-100"
              >
                {label}
              </Link>
            ))}
            <button
              onClick={handleLogout}
              className="mt-3 flex w-full items-center gap-2 border-t px-3 py-3 text-sm text-red-700"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-[1600px] px-4 py-6 lg:px-6">
        {children}
      </main>

      {/* Edit Profile Modal */}
      {showEditProfile && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-md border animate-in zoom-in-95 duration-200">
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Edit Profile</h3>
                <button
                  onClick={() => setShowEditProfile(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              </div>
              <form onSubmit={handleSaveProfile} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Full Name</label>
                  <input
                    type="text"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.full_name}
                    onChange={(e) =>
                      setFormData({ ...formData, full_name: e.target.value })
                    }
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email Address</label>
                  <input
                    type="email"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary opacity-50 cursor-not-allowed"
                    disabled
                    value={user?.email || ""}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Email cannot be changed directly.
                  </p>
                </div>
                <div className="pt-4 flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowEditProfile(false)}
                    className="px-4 py-2 text-sm font-medium hover:bg-accent rounded-md"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md disabled:opacity-50"
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
